import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_debounced_function } from '@/hooks/debounce/use_debounced_function';
import { use_slice_generation, use_slice_ui } from '@/hooks/store/use_slices';
import { HOOK_GRID2D_CELL__size } from '@/pages/_base/grid2d_cell/_hooks/HOOK_GRID2D_CELL__size';
import { LOCAL__ui_generation_grid2d_set_hovered_dimensions } from '@/state/ui/generation/LOCAL__ui_generation_grid2d_set_hovered_dimensions';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { FIELD__base } from '@shared/dto/generation/graph_invoke/_fields/_base/FIELD__base';
import { FIELD_PRIMITIVE__base } from '@shared/dto/generation/graph_invoke/_fields/_primitives/_base/FIELD_PRIMITIVE__base';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { DTO__grid2d_generation } from '@shared/dto/grid2d/DTO__grid2d_generation';
import { DTO__grid2d_cell_generation } from '@shared/dto/grid2d_cell/DTO__grid2d_cell_generation';
import { DTO__grid2d_virtual } from '@shared/dto/grid2d_virtual/DTO__grid2d_virtual';
import { EShortcutType } from '@shared/dto/hint_shortcuts/e_shortcut_type';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import React, { useCallback, useMemo, useState } from 'react';
import { CONSTANTS__client } from '../../../../constants/CONSTANTS__client';
import { GRID2D_CELL__generation } from '../../grid2d_cell/views/GRID2D_CELL__generation';
import { GRID2D_GALLERY__fixed_generation } from '../../grid2d_gallery_generation/GRID2D_GALLERY__fixed_generation';
import { WATCHER_GRID2D_GALLERY_GENERATION__cell_current_set } from '../../grid2d_gallery_generation/WATCHER_GRID2D_GALLERY_GENERATION__cell_current_set';
import { WATCHER__shortcut_grid2d_gallery_close } from '../../grid2d_sidebar_image_compare/_watchers/WATCHER__shortcut_grid2d_gallery_close';
import { ViewShortcutTrigger } from '../../shortcut_hint/ViewShortcutTrigger';
import { WATCHER_GRID2D__change_cell_size_by_scroll } from '../../watchers/grid2d/WATCHER_GRID2D__change_cell_size_by_scroll';
import { WatcherImageToTextTranslate } from '../../watchers/image_to_text/WatcherImageToTextTranslate';
import { GRID2D_BARS_bottom } from '../bars/GRID2D_BARS_bottom';

interface Props {
  grid2d: DTO__grid2d;
}

export function GRID2D__fixed_generation({ grid2d }: Props) {
  /*
   * STORE
   */
  const dispatch = use_dispatch();

  const { generation_current } = use_slice_generation(['generation_current']);
  const { graph } = generation_current.tool;

  const { g_hovered_grid2d_column, g_hovered_grid2d_row, g_hovered_grid2d_dimension_from_grid, global__grid2d_show_as_gallery } = use_slice_ui([
    'g_hovered_grid2d_column',
    'g_hovered_grid2d_row',
    'g_hovered_grid2d_dimension_from_grid',
    'global__grid2d_show_as_gallery',
  ]);

  /*
   * STATE
   */

  const ref_container = use_ref_with_fallback(ENUM__html_ref_stub.DIV);
  const ref_grid = use_ref_with_fallback(ENUM__html_ref_stub.DIV);

  const [grid_size_px, set_grid_size_px] = useState({ width: 0, height: 0 });

  const { cell_width, cell_height } = HOOK_GRID2D_CELL__size(generation_current);

  const grid2d_virtual = useMemo(() => DTO__grid2d_virtual.CREATE_STUB(), []);

  const virtualizer_row = useVirtualizer({
    horizontal: false,
    count: graph.grid2d__get_field_row().get_grid2d_side_size(),
    getScrollElement: () => ref_grid.current,
    estimateSize: () => cell_height + CONSTANTS__client.GENERATION_GRID_GAP,
    overscan: 2,
  });

  const virtualizer_column = useVirtualizer({
    horizontal: true,
    count: graph.grid2d__get_field_column().get_grid2d_side_size(),
    getScrollElement: () => ref_grid.current,
    estimateSize: () => cell_width + CONSTANTS__client.GENERATION_GRID_GAP,
    overscan: 2,
  });

  const on_mouse_leave_cell_cached = useCallback(on_mouse_leave_cell, []);
  const on_mouse_leave_cell_debounced = use_debounced_function(on_mouse_leave_cell_cached, 50);

  const on_mouse_enter_cell_cached = useCallback(on_mouse_enter_cell, []);
  const on_mouse_enter_cell_debounced = use_debounced_function(on_mouse_enter_cell_cached, 50);

  const update_grid_size_px_debounced = use_debounced_function(update_grid_size_px, 1300);

  const scroll_grid_by_hovered_dimension_column_cached = useCallback(scroll_grid_by_hovered_dimension_column, [
    [g_hovered_grid2d_column, graph, virtualizer_column],
  ]);
  const scroll_grid_by_hovered_dimension_column_debounced = use_debounced_function(scroll_grid_by_hovered_dimension_column_cached, 100);

  const scroll_grid_by_hovered_dimension_row_cached = useCallback(scroll_grid_by_hovered_dimension_row, [[g_hovered_grid2d_row, graph, virtualizer_row]]);
  const scroll_grid_by_hovered_dimension_row_debounced = use_debounced_function(scroll_grid_by_hovered_dimension_row_cached, 100);

  const [is_mouse_over, set_is_mouse_over] = useState(false);

  /*
   * EFFECTS
   */

  use_effect(() => virtualizer_column.measure(), [virtualizer_column, cell_width, grid2d, graph]);
  use_effect(() => virtualizer_row.measure(), [virtualizer_row, cell_height, grid2d, graph]);

  use_effect(() => {
    const observer = new ResizeObserver(() => update_grid_size_px_debounced());

    observer.observe(ref_container.current);

    return () => observer.disconnect();
  }, []);

  use_effect(() => {
    window.resize_on(update_grid_size_px_debounced);

    return () => window.resize_off(update_grid_size_px_debounced);
  }, [update_grid_size_px_debounced]);

  use_effect(() => {
    if (g_hovered_grid2d_dimension_from_grid) return;

    scroll_grid_by_hovered_dimension_column_debounced();
  }, [g_hovered_grid2d_column]);

  use_effect(() => {
    if (g_hovered_grid2d_dimension_from_grid) return;

    scroll_grid_by_hovered_dimension_row_debounced();
  }, [g_hovered_grid2d_row]);

  /*
   * EVENTS
   */

  function on_mouse_enter_cell(cell: DTO__grid2d_cell_generation) {
    let [row, column] = [cell.field_row, cell.field_column];
    dispatch(LOCAL__ui_generation_grid2d_set_hovered_dimensions({ column, row, g_hovered_grid2d_dimension_from_grid: true }));
  }

  function on_mouse_leave_cell(_: DTO__grid2d_cell_generation) {
    dispatch(
      LOCAL__ui_generation_grid2d_set_hovered_dimensions({
        column: FIELD__base.CREATE_STUB(),
        row: FIELD__base.CREATE_STUB(),
        g_hovered_grid2d_dimension_from_grid: false,
      }),
    );
  }

  /*
   * METHODS
   */

  function update_grid_size_px() {
    const { clientWidth: width, clientHeight: height } = ref_container.current;

    if (width !== grid_size_px.width || height !== grid_size_px.height) {
      set_grid_size_px({ width, height });
    }
  }

  function scroll_grid_by_hovered_dimension_column() {
    if (g_hovered_grid2d_column.is_stub()) return;
    if (!(g_hovered_grid2d_column instanceof FIELD_PRIMITIVE__base)) return;

    const index_column = graph.grid2d__get_field_column().grid2d_dimension_values.findIndex(value => value === g_hovered_grid2d_column.value);

    if (index_column === -1) return;

    virtualizer_column.scrollToIndex(index_column, { align: 'center', behavior: 'smooth' });
    virtualizer_column.measure();
  }

  function scroll_grid_by_hovered_dimension_row() {
    if (g_hovered_grid2d_row.is_stub()) return;
    if (!(g_hovered_grid2d_column instanceof FIELD_PRIMITIVE__base)) return;

    const index_row = graph.grid2d__get_field_row().grid2d_dimension_values.findIndex(value => value === g_hovered_grid2d_row.value);

    if (index_row === -1) return;

    virtualizer_row.scrollToIndex(index_row, { align: 'center', behavior: 'smooth' });
    virtualizer_row.measure();
  }

  /*
   * RENDERS
   */

  function render_cell(virtual_row: VirtualItem, virtual_column: VirtualItem) {
    return (
      <React.Fragment key={virtual_column.key}>
        <GRID2D_CELL__generation
          x={virtual_column.index}
          y={virtual_row.index}
          //
          {...{ cell_width, cell_height }}
          //
          on_mouse_enter_cell={on_mouse_enter_cell_debounced}
          on_mouse_leave_cell={on_mouse_leave_cell_debounced}
          grid2d_generation_current={grid2d}
        />
      </React.Fragment>
    );
  }

  function render_grid() {
    if (grid2d.is_stub()) return null;

    assert_instanceof(grid2d, DTO__grid2d_generation);

    if (global__grid2d_show_as_gallery) return <GRID2D_GALLERY__fixed_generation {...{ grid2d }} />;

    return (
      <div
        className="relative"
        style={{
          height: `${virtualizer_row.getTotalSize()}px`,
          width: `${virtualizer_column.getTotalSize()}px`,
          willChange: 'transform',
          // gap: `${CONSTANTS__client.GENERATION_GRID_GAP}px`,
        }}
      >
        {virtualizer_row.getVirtualItems().map(virtual_row => (
          <React.Fragment key={virtual_row.key}>
            {virtualizer_column.getVirtualItems().map(virtual_column => render_cell(virtual_row, virtual_column))}
          </React.Fragment>
        ))}
      </div>
    );
  }

  /*
   * HTML
   */

  if (generation_current.is_stub()) return null;
  if (grid2d.is_stub()) return null;

  return (
    <>
      <WATCHER__shortcut_grid2d_gallery_close />
      <WatcherImageToTextTranslate {...{ grid2d }} entity={generation_current} />
      <WATCHER_GRID2D__change_cell_size_by_scroll {...{ div_grid: ref_container.current }} />

      <WATCHER_GRID2D_GALLERY_GENERATION__cell_current_set {...{ grid2d }} />

      <ViewShortcutTrigger hint_shortcut_keys={[EShortcutType.GRID2D_CHANGE_CELL_SIZE_WITH_SCROLL]}>
        <div
          onMouseOver={() => set_is_mouse_over(true)}
          onMouseLeave={() => set_is_mouse_over(false)}
          ref={ref_container}
          className={`grid_2d bg-component flex flex-col size-full 
            max-h-full
            max-w-full
          relative cursor-default`}
        >
          <div
            ref={ref_grid}
            className="
            overflow-auto 

            size-full
            max-h-full
            max-w-full
            
            touch-none select-none
            "
          >
            {render_grid()}
          </div>
          <GRID2D_BARS_bottom entity={generation_current} div_grid={ref_grid.current} {...{ grid2d_virtual, grid2d }} />
        </div>
      </ViewShortcutTrigger>
    </>
  );
}
